import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { anotarVisita } from '@/lib/visitante';
import BotaoAvisos from '@/components/feature/BotaoAvisos';
import NumeroAnimado from '@/components/movimento/NumeroAnimado';
import { formatarCentavos, carteiraParaUsar } from '@/pages/loja/utils';
import { guardarQuantidades, lerRascunho } from '@/lib/rascunhoFichas';
import { formatarDataHoraCurta } from './utils';
import LinhaFicha from './components/LinhaFicha';
import ModalCheckout from '@/pages/loja/ModalCheckout';
import type { VitrineLoja, ProdutoVitrine } from './types';

interface ItemCarrinho {
  produto_id: number;
  nome: string;
  preco_centavos: number;
  quantidade: number;
}

type EstadoTela = 'carregando' | 'pronto' | 'erro';

// Azul-marinho profundo da tela de fichas (fundo sólido, nunca arte borrada).
const NAVY = '#10203A';
const NAVY_PE = '#0B1729';
const OURO = '#E8C877';

// "27/09" no horário de Brasília.
function diaCurto(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
  }).format(d);
}

export default function FichasPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [vitrine, setVitrine] = useState<VitrineLoja | null>(null);
  const [estado, setEstado] = useState<EstadoTela>('carregando');
  const [erro, setErro] = useState<string | null>(null);
  const [eventoId, setEventoId] = useState<number | null>(null);
  const [reload, setReload] = useState(0);

  const [carrinho, setCarrinho] = useState<Record<number, number>>({});
  // Só começa a gravar o rascunho depois de restaurar o que já estava escolhido.
  const [restaurado, setRestaurado] = useState(false);
  const restaurouRef = useRef(false);
  const [aba, setAba] = useState<string>('TODOS');
  const [checkoutAberto, setCheckoutAberto] = useState(false);
  const [nomeCarteira, setNomeCarteira] = useState<string | null>(null);
  const [carteiraCodigo, setCarteiraCodigo] = useState<string | null>(null);
  const visitaRegistrada = useRef(false);

  // Resolve slug → id e carrega a vitrine (uma chamada só, depois do id).
  useEffect(() => {
    let ativo = true;
    async function carregar() {
      setEstado('carregando');
      setErro(null);
      try {
        const { data: ev, error: errEv } = await supabase
          .from('eventos')
          .select('id')
          .eq('slug', slug)
          .maybeSingle();
        if (errEv) throw errEv;
        if (!ativo) return;
        if (!ev) {
          setEventoId(null);
          setVitrine(null);
          setEstado('erro');
          return;
        }
        const id = Number((ev as { id: number }).id);
        setEventoId(id);

        const { data: vit, error: errVit } = await supabase.rpc('loja_vitrine', {
          p_evento: id,
        });
        if (errVit) throw errVit;
        if (!ativo) return;
        setVitrine((vit as VitrineLoja) ?? null);
        setEstado('pronto');
      } catch (e) {
        if (ativo) {
          setErro(mensagemAmigavel(e, 'Não foi possível carregar as fichas.'));
          setEstado('erro');
        }
      }
    }
    carregar();
    return () => {
      ativo = false;
    };
  }, [slug, reload]);

  // Anota a visita (o QR das fichas é uma porta de entrada do evento).
  useEffect(() => {
    if (visitaRegistrada.current) return;
    if (!eventoId || !slug) return;
    visitaRegistrada.current = true;
    anotarVisita(slug);
  }, [eventoId, slug]);

  // Tempo real: quando o sinal da vitrine muda, releia (debounce 500 ms).
  useEffect(() => {
    if (!eventoId) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const canal = supabase
      .channel(`fichas-${eventoId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'caixa_sinais',
          filter: `evento_id=eq.${eventoId},origem=eq.vitrine`,
        },
        () => {
          if (timer) clearTimeout(timer);
          timer = setTimeout(() => setReload((r) => r + 1), 500);
        },
      )
      .subscribe();
    return () => {
      if (timer) clearTimeout(timer);
      supabase.removeChannel(canal);
    };
  }, [eventoId]);

  // Carteira salva neste celular: mostra "Olá, Maria · minha carteira".
  useEffect(() => {
    if (!eventoId) return;
    let ativo = true;
    (async () => {
      const salva = await carteiraParaUsar(eventoId);
      if (!ativo || !salva) return;
      setCarteiraCodigo(salva);
      try {
        const { data } = await supabase.rpc('loja_carteira_publica', { p_carteira: salva });
        if (!ativo) return;
        const nome = (data as { carteira?: { nome?: string } } | null)?.carteira?.nome;
        if (nome) setNomeCarteira(nome);
      } catch {
        /* segue sem o nome */
      }
    })();
    return () => {
      ativo = false;
    };
  }, [eventoId]);

  const pontos = vitrine?.pontos ?? [];
  const previa = vitrine?.previa === true;

  const todosProdutos = useMemo<ProdutoVitrine[]>(
    () => pontos.flatMap((p) => p.produtos),
    [pontos],
  );

  const gruposVisiveis = useMemo(() => {
    if (aba === 'TODOS') return pontos;
    return pontos.filter((p) => String(p.ponto_id) === aba);
  }, [pontos, aba]);

  // Volta com as quantidades que a pessoa já tinha escolhido neste evento.
  useEffect(() => {
    if (!eventoId || estado !== 'pronto' || restaurouRef.current) return;
    restaurouRef.current = true;
    const salvo = lerRascunho(eventoId);
    const novo: Record<number, number> = {};
    todosProdutos.forEach((p) => {
      const escolhido = salvo.qtd[p.produto_id];
      if (!escolhido || escolhido <= 0 || p.situacao === 'esgotou') return;
      let limite = Number.MAX_SAFE_INTEGER;
      if (p.max_por_pedido != null && p.max_por_pedido > 0) limite = p.max_por_pedido;
      if (p.disponivel != null && p.disponivel >= 0) limite = Math.min(limite, p.disponivel);
      const valor = Math.min(escolhido, limite);
      if (valor > 0) novo[p.produto_id] = valor;
    });
    if (Object.keys(novo).length > 0) setCarrinho(novo);
    setRestaurado(true);
  }, [eventoId, estado, todosProdutos]);

  // Cada toque em −/+ fica guardado: voltar para a tela não zera nada.
  useEffect(() => {
    if (!eventoId || !restaurado) return;
    guardarQuantidades(eventoId, carrinho);
  }, [eventoId, carrinho, restaurado]);

  const itensCarrinho = useMemo<ItemCarrinho[]>(() => {
    const lista: ItemCarrinho[] = [];
    Object.entries(carrinho).forEach(([id, qtd]) => {
      if (qtd <= 0) return;
      const p = todosProdutos.find((x) => x.produto_id === Number(id));
      if (p) {
        lista.push({
          produto_id: p.produto_id,
          nome: p.nome,
          preco_centavos: p.preco_centavos,
          quantidade: qtd,
        });
      }
    });
    return lista;
  }, [carrinho, todosProdutos]);

  const totalCentavos = useMemo(
    () => itensCarrinho.reduce((soma, i) => soma + i.preco_centavos * i.quantidade, 0),
    [itensCarrinho],
  );
  const totalQtd = useMemo(
    () => itensCarrinho.reduce((soma, i) => soma + i.quantidade, 0),
    [itensCarrinho],
  );

  function definirQuantidade(produto: ProdutoVitrine, v: number) {
    let limite = Number.MAX_SAFE_INTEGER;
    if (produto.max_por_pedido != null && produto.max_por_pedido > 0) {
      limite = produto.max_por_pedido;
    }
    if (produto.disponivel != null && produto.disponivel >= 0) {
      limite = Math.min(limite, produto.disponivel);
    }
    const valor = Math.min(Math.max(0, v), limite);
    setCarrinho((atual) => {
      const novo = { ...atual };
      if (valor === 0) delete novo[produto.produto_id];
      else novo[produto.produto_id] = valor;
      return novo;
    });
  }

  const qtdDe = (id: number) => carrinho[id] ?? 0;

  const evento = vitrine?.evento;
  const capa = evento?.capa || evento?.capa_desktop || null;

  // ————— Casca: fundo sólido escuro, sempre presente —————
  const casca = (conteudo: ReactNode) => (
    <div className="min-h-screen" style={{ background: NAVY }}>
      <div className="relative min-h-screen overflow-x-clip">{conteudo}</div>
    </div>
  );

  // Cabeçalho: voltar · nome do evento · data curta.
  const cabecalho = (
    <header className="relative z-20">
      <div className="mx-auto flex w-full max-w-[720px] items-center gap-3 px-4 pt-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label="Voltar"
          className="aperta flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/15"
        >
          <i className="ri-arrow-left-line text-[20px]" aria-hidden />
        </button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[14px] font-semibold leading-tight text-white">
            {evento?.nome ?? 'Fichas'}
          </p>
          {evento?.inicio_em && (
            <p className="truncate text-[12px] leading-tight text-white/55">
              {diaCurto(evento.inicio_em)}
            </p>
          )}
        </div>
      </div>
    </header>
  );

  if (estado === 'carregando') {
    return casca(
      <>
        <div className="h-14" />
        <div className="mx-auto w-full max-w-[720px] px-4 pt-3">
          <div className="h-[120px] w-full animate-pulse rounded-[16px] bg-white/[0.06]" />
          <div className="mt-4 h-5 w-24 animate-pulse rounded-full bg-white/[0.08]" />
          <div className="mt-2 h-3 w-52 animate-pulse rounded-full bg-white/[0.06]" />
          <div className="mt-5 space-y-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-[92px] w-full animate-pulse rounded-[16px] bg-white/[0.06]" />
            ))}
          </div>
        </div>
      </>,
    );
  }

  if (estado === 'erro' || !vitrine || !evento) {
    return casca(
      <>
        {cabecalho}
        <div className="mx-auto w-full max-w-[720px] px-4 py-10 text-center">
          <div className="rounded-[16px] border border-white/10 bg-white/[0.04] p-8">
            <p className="text-[14px] text-white/80">{erro ?? 'Não encontramos estas fichas.'}</p>
            <Link
              to={slug ? `/e/${slug}` : '/'}
              className="mt-5 inline-flex h-12 cursor-pointer items-center justify-center whitespace-nowrap rounded-[14px] bg-white/10 px-6 text-[14px] font-semibold text-white transition-colors hover:bg-white/15"
            >
              Ver o evento
            </Link>
          </div>
        </div>
      </>,
    );
  }

  // inexistente / sem_loja: volta para a página do evento.
  if (vitrine.estado === 'inexistente' || vitrine.estado === 'sem_loja') {
    return <Navigate to={slug ? `/e/${slug}` : '/'} replace />;
  }

  const encerrada = vitrine.estado === 'encerrada';
  const temPontos = pontos.length > 0;
  const gradeVisivel = !encerrada && temPontos;
  const mostraLinhaAbre = vitrine.estado === 'fechada' || vitrine.estado === 'agendada';

  return casca(
    <>
      {previa && (
        <div className="fixed inset-x-0 top-0 z-50 bg-warning-500">
          <div className="mx-auto w-full max-w-[720px] px-4 py-2.5">
            <p className="flex items-center gap-2 text-[13px] font-semibold text-white">
              <span className="flex h-5 w-5 items-center justify-center">
                <i className="ri-eye-line" aria-hidden />
              </span>
              Prévia — só você vê. O evento ainda não foi publicado.
            </p>
          </div>
        </div>
      )}

      <div className={`app-entra ${previa ? 'pt-[52px]' : ''}`}>
        {cabecalho}

        {/* Arte do evento: só uma faixa de 120 px, sem desfoque */}
        {capa && (
          <div className="mx-auto mt-3 w-full max-w-[720px] px-4">
            <div className="h-[120px] w-full overflow-hidden rounded-[16px] bg-white/5">
              <img
                src={capa}
                alt={evento.nome}
                className="h-full w-full object-cover object-top"
                decoding="async"
              />
            </div>
          </div>
        )}

        <div className="mx-auto w-full max-w-[720px] px-4 pt-4">
          <h1 className="text-[22px] font-extrabold leading-tight text-white">Fichas</h1>
          <p className="mt-0.5 text-[13px] text-white/60">
            Compre aqui e mostre o código na barraca
          </p>
        </div>

        {/* Carteira deste aparelho */}
        {nomeCarteira && carteiraCodigo && (
          <div className="mx-auto mt-3 w-full max-w-[720px] px-4">
            <Link
              to={`/carteira/${carteiraCodigo}`}
              className="aperta inline-flex max-w-full cursor-pointer items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-3.5 py-2 text-[13px] text-white/80 transition-colors hover:bg-white/[0.1]"
            >
              <span className="flex h-4 w-4 items-center justify-center text-[#E8C877]">
                <i className="ri-wallet-3-line text-[14px]" aria-hidden />
              </span>
              <span className="truncate font-semibold text-white">Olá, {nomeCarteira}</span>
              <span className="text-white/40" aria-hidden>
                ·
              </span>
              <span className="whitespace-nowrap underline">minha carteira</span>
            </Link>
          </div>
        )}

        {encerrada ? (
          <div className="mx-auto mt-6 w-full max-w-[720px] px-4">
            <div className="rounded-[16px] border border-white/10 bg-white/[0.04] p-8 text-center">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-white/[0.08] text-white/70">
                <i className="ri-calendar-check-line text-[22px]" aria-hidden />
              </span>
              <h2 className="mt-4 text-[17px] font-extrabold text-white">Este evento já terminou</h2>
              <p className="mt-1.5 text-[13px] text-white/60">Obrigado por participar!</p>
              {carteiraCodigo && (
                <Link
                  to={`/carteira/${carteiraCodigo}`}
                  className="mt-5 inline-flex h-12 w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-[14px] px-6 text-[14px] font-bold"
                  style={{ background: OURO, color: NAVY }}
                >
                  Minha carteira
                </Link>
              )}
            </div>
          </div>
        ) : !gradeVisivel ? (
          <div className="mx-auto mt-6 w-full max-w-[720px] px-4">
            <div className="rounded-[16px] border border-white/10 bg-white/[0.04] p-8 text-center">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-white/[0.08] text-[#E8C877]">
                <i className="ri-time-line text-[22px]" aria-hidden />
              </span>
              <h2 className="mt-4 text-[17px] font-extrabold text-white">
                As vendas ainda não abriram.
              </h2>
              {vitrine.abre_em && (
                <p className="mt-1.5 text-[13px] text-white/60">
                  Abrem {formatarDataHoraCurta(vitrine.abre_em)}
                </p>
              )}
              {mostraLinhaAbre && eventoId != null && (
                <div className="mt-5 flex justify-center">
                  <BotaoAvisos
                    eventoId={eventoId}
                    carteiraCodigo={carteiraCodigo}
                    rotulo="Me avise quando as fichas abrirem"
                  />
                </div>
              )}
              <Link
                to={slug ? `/e/${slug}` : '/'}
                className="mt-4 inline-flex h-12 w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-[14px] bg-white/10 px-6 text-[14px] font-semibold text-white transition-colors hover:bg-white/15"
              >
                Ver o evento
              </Link>
            </div>
          </div>
        ) : (
          <>
            {/* Chips só quando existe mais de uma barraca */}
            {pontos.length > 1 && (
              <div className="mx-auto mt-4 w-full max-w-[720px]">
                <div className="flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                  <button
                    type="button"
                    onClick={() => setAba('TODOS')}
                    className={`aperta h-9 shrink-0 cursor-pointer whitespace-nowrap rounded-full px-3.5 text-[12.5px] font-bold transition-colors ${
                      aba === 'TODOS'
                        ? 'bg-white/15 text-white'
                        : 'border border-white/10 text-white/65 hover:bg-white/[0.06]'
                    }`}
                  >
                    TODOS
                  </button>
                  {pontos.map((p) => (
                    <button
                      key={p.ponto_id}
                      type="button"
                      onClick={() => setAba(String(p.ponto_id))}
                      className={`aperta h-9 shrink-0 cursor-pointer whitespace-nowrap rounded-full px-3.5 text-[12.5px] font-bold transition-colors ${
                        aba === String(p.ponto_id)
                          ? 'bg-white/15 text-white'
                          : 'border border-white/10 text-white/65 hover:bg-white/[0.06]'
                      }`}
                    >
                      {p.nome}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div
              className="mx-auto mt-3 w-full max-w-[720px] space-y-4 px-4"
              style={{ paddingBottom: '128px' }}
            >
              {gruposVisiveis.map((g) => (
                <section key={g.ponto_id}>
                  <div className="flex items-center gap-2 pb-2">
                    <span
                      className="flex h-6 w-6 items-center justify-center rounded-[8px] bg-white/[0.08]"
                      style={{ color: OURO }}
                    >
                      <i className="ri-store-2-line text-[13px]" aria-hidden />
                    </span>
                    <h2 className="truncate text-[12px] font-bold uppercase tracking-[0.06em] text-white/65">
                      {g.nome}
                    </h2>
                  </div>
                  <div className="space-y-2">
                    {g.produtos.map((p) => (
                      <LinhaFicha
                        key={p.produto_id}
                        produto={p}
                        qtd={qtdDe(p.produto_id)}
                        previa={previa}
                        aoMudarQtd={definirQuantidade}
                      />
                    ))}
                  </div>
                </section>
              ))}

              {/* Uma única barraca com uma única ficha: explica o caminho. */}
              {pontos.length === 1 && pontos[0].produtos.length === 1 && (
                <div className="rounded-[16px] border border-white/10 bg-white/[0.04] p-4">
                  <p
                    className="text-[11px] font-bold uppercase tracking-[0.08em]"
                    style={{ color: OURO }}
                  >
                    Como funciona
                  </p>
                  <ul className="mt-2 space-y-1.5 text-[13px] leading-snug text-white/70">
                    <li>1 · Escolha quantas fichas</li>
                    <li>2 · Pague no Pix</li>
                    <li>3 · Mostre o QR na barraca e retire</li>
                  </ul>
                </div>
              )}

              <p className="pt-1 text-[12px] leading-relaxed text-white/40">
                Cada ficha ajuda {evento.nome}. Obrigado!
              </p>

              {mostraLinhaAbre && vitrine.abre_em && (
                <div className="pt-1 text-center">
                  <p className="text-[12px] text-white/50">
                    Mais fichas a partir de {formatarDataHoraCurta(vitrine.abre_em)}
                  </p>
                  {eventoId != null && (
                    <div className="mt-3 flex justify-center">
                      <BotaoAvisos
                        eventoId={eventoId}
                        carteiraCodigo={carteiraCodigo}
                        rotulo="Me avise quando as fichas abrirem"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Rodapé fixo: sempre visível na venda */}
      {gradeVisivel && (
        <div className="fixed inset-x-0 bottom-0 z-40" style={{ background: NAVY_PE }}>
          <div
            className="mx-auto flex w-full max-w-[720px] items-center gap-3 px-4 py-3"
            style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
          >
            <p className="min-w-0 flex-1 truncate text-[14px] font-semibold text-white">
              {totalQtd > 0 ? (
                <>
                  {totalQtd} {totalQtd === 1 ? 'ficha' : 'fichas'}
                  <span style={{ color: OURO }}>
                    {' · '}
                    <NumeroAnimado valor={formatarCentavos(totalCentavos)} />
                  </span>
                </>
              ) : (
                <span className="text-white/50">Nenhuma ficha ainda</span>
              )}
            </p>
            <button
              type="button"
              onClick={() => setCheckoutAberto(true)}
              disabled={totalQtd === 0 || previa}
              className={`h-[52px] shrink-0 whitespace-nowrap rounded-[14px] px-7 text-[15px] font-bold ${
                totalQtd > 0 && !previa ? 'aperta cursor-pointer' : 'cursor-not-allowed'
              }`}
              style={
                totalQtd > 0 && !previa
                  ? {
                      background: OURO,
                      color: NAVY,
                      transition: 'background-color 200ms ease, color 200ms ease',
                    }
                  : {
                      background: 'rgba(255,255,255,.10)',
                      color: 'rgba(255,255,255,.35)',
                      transition: 'background-color 200ms ease, color 200ms ease',
                    }
              }
            >
              Continuar
            </button>
          </div>
        </div>
      )}

      {checkoutAberto && eventoId != null && (
        <ModalCheckout
          eventoId={eventoId}
          eventoNome={evento.nome}
          eventoImagem={capa}
          itens={itensCarrinho}
          totalCentavos={totalCentavos}
          quemPagaTaxa={vitrine.quem_paga_taxa}
          aoFechar={() => setCheckoutAberto(false)}
        />
      )}
    </>,
  );
}